import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  ZoomIn, 
  ZoomOut, 
  Layers, 
  Filter,
  Maximize2,
  Minimize2,
  RefreshCw,
  Eye,
  EyeOff,
  Info,
  X
} from 'lucide-react';
import { GeoThreat } from '../../data/mockData';

interface RealTimeMapProps {
  threats: GeoThreat[];
}

const RealTimeMap: React.FC<RealTimeMapProps> = ({ threats }) => {
  const [selectedThreat, setSelectedThreat] = useState<GeoThreat | null>(null);
  const [mapView, setMapView] = useState<'satellite' | 'terrain' | 'street'>('terrain');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(new Set([
    'natural_disaster', 'human_health', 'war_conflict', 'animal_health', 
    'plant_agriculture', 'environmental', 'technological'
  ]));
  const [zoomLevel, setZoomLevel] = useState(2);
  const [centerPosition, setCenterPosition] = useState({ lat: 20, lng: 0 });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  const categoryColors = {
    natural_disaster: '#ef4444',
    human_health: '#f97316', 
    war_conflict: '#dc2626',
    animal_health: '#059669',
    plant_agriculture: '#65a30d',
    environmental: '#0891b2',
    technological: '#7c3aed'
  };

  const categoryLabels = {
    natural_disaster: 'Natural Disasters',
    human_health: 'Human Health',
    war_conflict: 'War & Conflict', 
    animal_health: 'Animal Health',
    plant_agriculture: 'Agriculture',
    environmental: 'Environmental',
    technological: 'Technology'
  };

  const getSeveritySize = (severity: string) => {
    switch (severity) {
      case 'critical': return 'w-6 h-6';
      case 'high': return 'w-5 h-5';
      case 'medium': return 'w-4 h-4';
      case 'low': return 'w-3 h-3';
      default: return 'w-4 h-4';
    }
  };

  const getStatusPulse = (status: string) => {
    return status === 'active' ? 'animate-pulse' : '';
  };

  const filteredThreats = threats.filter(threat => 
    visibleCategories.has(threat.category)
  );

  const toggleCategory = (category: string) => {
    const newVisible = new Set(visibleCategories);
    if (newVisible.has(category)) {
      newVisible.delete(category);
    } else {
      newVisible.add(category);
    }
    setVisibleCategories(newVisible);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 1, 10));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 1, 1));
  };

  const resetView = () => {
    setZoomLevel(2);
    setCenterPosition({ lat: 20, lng: 0 });
  };

  // Convert lat/lng to map coordinates
  const getMapPosition = (lat: number, lng: number) => {
    const x = ((lng + 180) / 360) * 100;
    const y = ((90 - lat) / 180) * 100;
    return { x, y };
  };

  return (
    <motion.div
      layout
      className={`bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl overflow-hidden ${
        isFullscreen ? 'fixed inset-4 z-50' : 'h-96'
      }`}
      initial={false}
      animate={{
        scale: isFullscreen ? 1 : 1,
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {/* Map Header */}
      <div className="p-4 border-b border-dark-700 flex items-center justify-between bg-dark-900/50">
        <div className="flex items-center space-x-3">
          <MapPin className="w-5 h-5 text-primary-400" />
          <h3 className="text-lg font-semibold">Real-Time Threat Map</h3>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-accent-green rounded-full animate-pulse"></div>
            <span className="text-xs text-gray-400">Live</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Refresh */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleRefresh}
            className="p-2 hover:bg-dark-700 rounded transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </motion.button>

          {/* View Toggle */}
          <select
            value={mapView}
            onChange={(e) => setMapView(e.target.value as any)}
            className="bg-dark-700 border border-dark-600 rounded px-3 py-1 text-sm focus:outline-none focus:border-primary-400"
          >
            <option value="terrain">Terrain</option>
            <option value="satellite">Satellite</option>
            <option value="street">Street</option>
          </select>
          
          {/* Map Controls */}
          <button 
            onClick={handleZoomIn}
            className="p-2 hover:bg-dark-700 rounded transition-colors"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button 
            onClick={handleZoomOut}
            className="p-2 hover:bg-dark-700 rounded transition-colors"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          
          {/* Fullscreen Toggle */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 hover:bg-dark-700 rounded transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="flex h-full">
        {/* Category Filter Sidebar */}
        <div className="w-64 bg-dark-900/30 border-r border-dark-700 p-4 overflow-y-auto">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Threat Categories
          </h4>
          
          <div className="space-y-2">
            {Object.entries(categoryLabels).map(([category, label]) => (
              <motion.div
                key={category}
                whileHover={{ scale: 1.02 }}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-dark-700/50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: categoryColors[category as keyof typeof categoryColors] }}
                  />
                  <span className="text-sm text-gray-300">{label}</span>
                </div>
                <button
                  onClick={() => toggleCategory(category)}
                  className="p-1 hover:bg-dark-600 rounded transition-colors"
                >
                  {visibleCategories.has(category) ? (
                    <Eye className="w-3 h-3 text-accent-green" />
                  ) : (
                    <EyeOff className="w-3 h-3 text-gray-500" />
                  )}
                </button>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-6 pt-4 border-t border-dark-700">
            <h5 className="text-xs font-semibold text-gray-400 mb-2">VISIBLE THREATS</h5>
            <div className="text-2xl font-bold text-primary-400 mb-1">
              {filteredThreats.length}
            </div>
            <div className="text-xs text-gray-500">
              of {threats.length} total
            </div>
          </div>

          {/* Reset View */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={resetView}
            className="w-full mt-4 bg-dark-700 hover:bg-dark-600 px-3 py-2 rounded-lg text-sm transition-colors"
          >
            Reset View
          </motion.button>
        </div>

        {/* Map Area */}
        <div className="flex-1 relative overflow-hidden">
          {/* Map Background */}
          <div 
            ref={mapRef}
            className={`absolute inset-0 transition-all duration-300 ${
              mapView === 'satellite' 
                ? 'bg-gradient-to-br from-blue-900 via-green-900 to-yellow-900' 
                : mapView === 'terrain'
                ? 'bg-gradient-to-br from-blue-800 via-green-800 to-yellow-800'
                : 'bg-gradient-to-br from-gray-800 via-gray-700 to-gray-600'
            }`}
            style={{
              transform: `scale(${zoomLevel * 0.5}) translate(${-centerPosition.lng}px, ${-centerPosition.lat}px)`
            }}
          >
            {/* Grid Pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg viewBox="0 0 1000 500" className="w-full h-full">
                <defs>
                  <pattern id="map-grid" width="50" height="50" patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#map-grid)" />
              </svg>
            </div>

            {/* Continents Outline */}
            <div className="absolute inset-0 opacity-20">
              <svg viewBox="0 0 1000 500" className="w-full h-full">
                {/* Simplified continent shapes */}
                <path d="M150 180 Q200 160 250 180 Q300 200 280 250 Q250 280 200 270 Q150 250 150 180" 
                      fill="currentColor" opacity="0.3" />
                <path d="M480 160 Q520 140 560 160 Q580 180 570 220 Q550 240 520 235 Q480 220 480 160" 
                      fill="currentColor" opacity="0.3" />
                <path d="M600 140 Q680 120 750 140 Q780 160 770 200 Q750 220 700 215 Q650 200 600 140" 
                      fill="currentColor" opacity="0.3" />
                <path d="M750 280 Q780 270 810 280 Q820 300 815 320 Q800 330 780 325 Q760 310 750 280" 
                      fill="currentColor" opacity="0.3" />
                <path d="M250 280 Q280 270 310 280 Q330 300 325 340 Q300 360 275 355 Q250 340 250 280" 
                      fill="currentColor" opacity="0.3" />
              </svg>
            </div>

            {/* Threat Markers */}
            {filteredThreats.map((threat, index) => {
              const position = getMapPosition(threat.coordinates[1], threat.coordinates[0]);
              const categoryColor = categoryColors[threat.category as keyof typeof categoryColors];
              
              return (
                <motion.div
                  key={threat.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-10"
                  style={{ 
                    left: `${position.x}%`, 
                    top: `${position.y}%`,
                    transform: `translate(-50%, -50%) scale(${Math.max(0.5, zoomLevel * 0.2)})`
                  }}
                  onClick={() => setSelectedThreat(threat)}
                >
                  {/* Outer Ring (Category) */}
                  <motion.div
                    animate={{ 
                      scale: threat.status === 'active' ? [1, 1.3, 1] : 1,
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: threat.status === 'active' ? Infinity : 0,
                      delay: index * 0.2 
                    }}
                    className={`rounded-full border-2 flex items-center justify-center ${getSeveritySize(threat.severity)} ${getStatusPulse(threat.status)}`}
                    style={{ 
                      borderColor: categoryColor,
                      backgroundColor: `${categoryColor}20`
                    }}
                  >
                    {/* Inner Dot (Severity) */}
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: categoryColor }}
                    />
                  </motion.div>
                  
                  {/* Hover Label */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-dark-900/90 border border-dark-600 rounded text-xs whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity pointer-events-none z-20">
                    <div className="font-semibold">{threat.location}</div>
                    <div className="text-gray-400 capitalize">{threat.type.replace('_', ' ')}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Zoom Level Indicator */}
          <div className="absolute top-4 right-4 bg-dark-900/80 backdrop-blur-sm border border-dark-600 rounded-lg px-3 py-2">
            <div className="text-sm font-semibold text-white">
              Zoom: {zoomLevel}x
            </div>
          </div>

          {/* Coordinates Display */}
          <div className="absolute bottom-4 left-4 bg-dark-900/80 backdrop-blur-sm border border-dark-600 rounded-lg px-3 py-2">
            <div className="text-xs text-gray-400">
              Center: {centerPosition.lat.toFixed(1)}°, {centerPosition.lng.toFixed(1)}°
            </div>
          </div>
        </div>
      </div>

      {/* Selected Threat Modal */}
      <AnimatePresence>
        {selectedThreat && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-30"
            onClick={() => setSelectedThreat(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-dark-800 border border-dark-600 rounded-xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold capitalize">
                  {selectedThreat.type.replace('_', ' ')}
                </h3>
                <div className="flex items-center space-x-2">
                  <span 
                    className="px-2 py-1 rounded text-xs font-medium text-white"
                    style={{ 
                      backgroundColor: categoryColors[selectedThreat.category as keyof typeof categoryColors] 
                    }}
                  >
                    {categoryLabels[selectedThreat.category as keyof typeof categoryLabels]}
                  </span>
                  <button
                    onClick={() => setSelectedThreat(null)}
                    className="p-1 hover:bg-dark-700 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-400">Location</p>
                  <p className="text-white">{selectedThreat.location}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-400">Impact</p>
                  <p className="text-white">{selectedThreat.impactedCount.toLocaleString()} affected</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-400">Status</p>
                  <p className={`capitalize ${
                    selectedThreat.status === 'active' ? 'text-accent-red' : 
                    selectedThreat.status === 'monitoring' ? 'text-accent-orange' : 'text-accent-green'
                  }`}>
                    {selectedThreat.status}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-400">Severity</p>
                  <p className={`capitalize ${
                    selectedThreat.severity === 'critical' ? 'text-accent-red' :
                    selectedThreat.severity === 'high' ? 'text-accent-orange' :
                    selectedThreat.severity === 'medium' ? 'text-accent-yellow' : 'text-accent-green'
                  }`}>
                    {selectedThreat.severity}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-400">Description</p>
                  <p className="text-gray-300 text-sm">{selectedThreat.description}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default RealTimeMap;